import React, { useState, useMemo, useEffect } from 'react'
import classnames from 'classnames'
import PropTypes from 'prop-types'

import { useEventFeedsContext } from '../context/index.jsx'

import ScaleAndCoverage from './scale-and-coverage/index.jsx'
import Distribution from './distribution/index.jsx'
import Trends from './trends/index.jsx'
import { useIsSnapshot } from '../manage-component/index.jsx'
import VizualizationOnboardTooltips from '../visualization-onboard-tooltips/index.jsx'
import { TabItem } from '../../../components/tab-item'
import Geo from './highlights/geo/index.jsx'
import KeywordsCloud from './kecc/index.jsx'
import TopDomains from './highlights/domains/index.jsx'
import { TYPE_PREBUILT_ATTRIBUTES_V2 } from '../wizard/index.jsx'

import './styles.scss'

const SIDE_PANEL_STYLE = { maxWidth: '100%', position: 'relative' }

const VisualizationPanel = ({
  className,
  match: {
    params: { id },
  },
  panelWidth,
  visualizationOnboardShown,
  onVisualizationOnboardFinish,
}) => {
  const { data, permissions } = useEventFeedsContext()

  const TABS = {
    ...(permissions?.tabs?.scale_and_coverage?.display && { SCALE_COVERAGE: 'scale-and-coverage' }),
    ...(permissions?.tabs?.geo?.display &&
      data.type !== TYPE_PREBUILT_ATTRIBUTES_V2 && { GEO: 'geo' }),
    ...(permissions?.tabs?.distribution?.display &&
      data.type !== TYPE_PREBUILT_ATTRIBUTES_V2 && { DISTRIBUTION: 'distribution' }),
    ...(permissions?.tabs?.keywords?.display &&
      data.type !== TYPE_PREBUILT_ATTRIBUTES_V2 && { KEYWORDS: 'keywords' }),
    ...(permissions?.tabs?.trends?.display &&
      data.type !== TYPE_PREBUILT_ATTRIBUTES_V2 && { TRENDS: 'trends' }),
    ...(permissions?.tabs?.top_domains?.display &&
      data.type !== TYPE_PREBUILT_ATTRIBUTES_V2 && { TOP_DOMAINS: 'top-domains' }),
  }

  const TAB_NAMES = {
    'scale-and-coverage': 'Scale and Coverage',
    geo: 'Geo',
    distribution: 'Distribution',
    keywords: 'Keywords',
    trends: 'Trends',
    'top-domains': 'Top Domains',
  }

  const isSnapshot = useIsSnapshot()

  const requestConfig = useMemo(() => {
    return data.name
      ? {
          attributes: data.attributes,
          filters: data.filters,
        }
      : undefined
  }, [data])

  const [selectedTab, setSelectedTab] = useState([])

  const pathSplit = window.location.pathname.split('/')
  const section = pathSplit[3] || Object.values(TABS)[0] || ''
  const sectionSplit = section.split('_')
  const section1 = sectionSplit[0]
  const section2 = sectionSplit[1]

  useEffect(() => {
    if (permissions) {
      setSelectedTab(section1)
    }
  }, [permissions, section1])

  return (
    <section className={classnames('exploration', className)}>
      <div className="exploration__tabs">
        <For
          each="tab"
          index="index"
          of={Object.values(TABS).filter((tab) => (tab !== TABS.TRENDS ? true : !isSnapshot))}
        >
          <TabItem
            key={tab}
            item={TAB_NAMES[tab]}
            isSelected={tab === selectedTab}
            onClick={() => {
              window.history.pushState(null, '', `/data-explorer/${id}/${tab}`)
              setSelectedTab(tab)
            }}
          />
        </For>
      </div>
      <div className="exploration__content">
        <Choose>
          <When condition={selectedTab === TABS.SCALE_COVERAGE}>
            <ScaleAndCoverage />
          </When>
          <When condition={selectedTab === TABS.GEO}>
            <Geo />
          </When>
          <When condition={selectedTab === TABS.DISTRIBUTION}>
            <Distribution />
          </When>
          <When condition={selectedTab === TABS.KEYWORDS}>
            <KeywordsCloud />
          </When>
          <When condition={selectedTab === TABS.TRENDS && !isSnapshot}>
            <Trends id={id} section1={section1} section2={section2} requestConfig={requestConfig} />
          </When>
          <When condition={selectedTab === TABS.TOP_DOMAINS}>
            <TopDomains />
          </When>
          <Otherwise>{false}</Otherwise>
        </Choose>
        <div style={SIDE_PANEL_STYLE} id="optionsPanel" />
      </div>
      <If condition={visualizationOnboardShown}>
        <VizualizationOnboardTooltips
          panelWidth={panelWidth}
          onFinish={onVisualizationOnboardFinish}
        />
      </If>
    </section>
  )
}

VisualizationPanel.propTypes = {
  className: PropTypes.string,
}

VisualizationPanel.defaultProps = {
  className: '',
}

export default VisualizationPanel
